import {
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  GKRadio,
  GKSearchSelect,
  GKTextField,
  Grid2,
  RupeeIcon,
  Show,
} from "components";
import GKCheckbox from "components/GKCheckbox";
import { FeePlansForm } from "interfaces/Revenue";
import { Controller, useFormContext } from "react-hook-form";
import { validationForPercentage } from "utils/helpers";
import {
  BILLING_FREQUENCY,
  HURDLE_TYPE,
  PERFORMANCE_PLAN_FEE_PARAMETER,
} from "../utils";

const PerformanceFeeForm = () => {
  const form = useFormContext<FeePlansForm>();

  const watchHurdle = form.watch("performanceFee.hurdle");

  return (
    <Grid2 container mt={2} spacing={3}>
      <Grid2 size={12}>
        <Typography fontSize={14} fontWeight={700} color=" #20272A">
          Performance Fee
        </Typography>
      </Grid2>

      <Grid2 container size={12} spacing={0}>
        <Grid2 size={12}>
          <Controller
            name="performanceFee.minesManagementFee"
            control={form.control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <GKCheckbox
                    {...field}
                    disableLeftPadding
                    checked={field.value}
                  />
                }
                label={"Management Fee Deduction"}
              />
            )}
          />
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="performanceFee.highWatermark"
            control={form.control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <GKCheckbox
                    {...field}
                    disableLeftPadding
                    checked={field.value}
                  />
                }
                label={"High Water Mark"}
              />
            )}
          />
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="performanceFee.hurdle"
            control={form.control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <GKCheckbox
                    {...field}
                    disableLeftPadding
                    checked={field.value}
                  />
                }
                label={"Hurdle"}
              />
            )}
          />
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="performanceFee.includeDividend"
            control={form.control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <GKCheckbox
                    {...field}
                    disableLeftPadding
                    checked={field.value}
                  />
                }
                label={"Include Dividend"}
              />
            )}
          />
        </Grid2>
      </Grid2>

      <Grid2 size={6} container spacing={3}>
        <Grid2 size={6}>
          <Controller
            name="performanceFee.name"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                value={field?.value ?? ""}
                placeholder="Product Name"
                tooltipTitle="This name will appear on the invoices generated for your clients."
                inputLabel={"Product Name"}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="performanceFee.hsnSacCode"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please enter HSN code",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                value={field?.value ?? ""}
                placeholder="HSN Code"
                requiredField
                inputLabel={"HSN Code"}
                tooltipTitle="This code will appear on invoices to classify your product for tax compliance."
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            name="performanceFee.feeParameters"
            control={form.control}
            render={({ field }) => (
              <FormControl>
                <FormLabel
                  sx={{ fontSize: 13, fontWeight: 600, color: "#222" }}
                >
                  Fee Parameter
                </FormLabel>
                <RadioGroup row {...field}>
                  {PERFORMANCE_PLAN_FEE_PARAMETER.map((item) => {
                    return (
                      <FormControlLabel
                        key={item.value}
                        value={item.value}
                        control={<GKRadio disableLeftPadding />}
                        label={item.name}
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            )}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            name="performanceFee.fee"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please enter Fee %",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                value={field?.value ?? ""}
                requiredField
                inputLabel="Fee"
                type="number"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(
                    validationForPercentage(e.target.valueAsNumber),
                  );
                }}
                error={!!error}
                helperText={error?.message}
                slotProps={{ input: { endAdornment: "%" } }}
              />
            )}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            name="performanceFee.billingFrequency"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please select Billing Frequency",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSearchSelect
                {...field}
                requiredField
                inputLabel="Billing Frequency"
                disableClearable={false}
                options={BILLING_FREQUENCY || []}
                onChange={(e, val) => field.onChange(val?.value)}
                value={
                  BILLING_FREQUENCY.find(
                    (dataItem) => dataItem.value === field.value,
                  ) || ""
                }
                getOptionLabel={(option) => option.name || option}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            name="performanceFee.minimumFee"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                value={field?.value ?? ""}
                inputLabel="Minimum Fee"
                type="number"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e.target.valueAsNumber);
                }}
                slotProps={{ input: { endAdornment: <RupeeIcon /> } }}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="performanceFee.maximumFee"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                value={field?.value ?? ""}
                inputLabel="Maximum Fee"
                type="number"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e.target.valueAsNumber);
                }}
                slotProps={{ input: { endAdornment: <RupeeIcon /> } }}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="performanceFee.outOfPocketExpense"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                value={field?.value ?? ""}
                inputLabel="Out Of Pocket Expense"
                type="number"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e.target.valueAsNumber);
                }}
                slotProps={{ input: { endAdornment: "" } }}
              />
            )}
          />
        </Grid2>
        <Show.When isTrue={!!watchHurdle}>
          <Grid2 size={6}>
            <Controller
              name="performanceFee.hurdleType"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  inputLabel="Hurdle Type"
                  disableClearable={false}
                  options={HURDLE_TYPE || []}
                  onChange={(e, val) => field.onChange(val?.value)}
                  value={
                    HURDLE_TYPE.find(
                      (dataItem) => dataItem.value === field.value,
                    ) || ""
                  }
                  getOptionLabel={(option) => option.name || option}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="performanceFee.hurdleRate"
              control={form.control}
              render={({ field }) => (
                <GKTextField
                  {...field}
                  value={field?.value ?? ""}
                  inputLabel="Hurdle Rate"
                  type="number"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e.target.valueAsNumber);
                  }}
                />
              )}
            />
          </Grid2>
        </Show.When>
      </Grid2>
    </Grid2>
  );
};

export default PerformanceFeeForm;
